import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect, useContext } from "react";
import { useToaster, Uploader } from "rsuite";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { UserContext } from "../../_context/UserContext";
import Card from "../../../../ui/Card/Card";
import Button from "../../../../ui/Button/Button";
import Input from "../../../../ui/Input/Input";
import SectionRibbon from "../../../../ui/SectionRibbon/SectionRibbon";
import Badge from "../../../../ui/Badge/Badge";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";
import { Icon_Upload } from "../../../../../images/icons/Icon_Upload";
import SettingIcon from "@rsuite/icons/Setting";

export const CustomerSettingsFC = (props) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const [customerName, setCustomerName] = useState("");
  const [customerID, setCustomerID] = useState("");
  const [customerLogo, setCustomerLogo] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const { userRoles } = useContext(UserContext);

  const prefillFormFields = () => {
    setCustomerID(props.customer?.id);

    if (props.customer?.name) {
      setCustomerName(props.customer?.name);
    }

    if (props.customer?.logo_src) {
      setCustomerLogo(props.customer?.logo_src);
      setLogoPreview(props.customer?.logo_src);
    }

    setLogoFile(null);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      let logoUrl = customerLogo;

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("customer_id", customerID);

        const uploadResponse = await axiosInstance({
          method: "POST",
          url: `/customer/upload-logo`,
          headers: {
            "Content-Type": "multipart/form-data",
            authorization: `Bearer ${token}`,
          },
          data: formData,
        });
        logoUrl = uploadResponse.data.logoUrl;
      }

      await axiosInstance({
        method: "POST",
        url: `/customer/update/client/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: customerID,
          customer_name: customerName,
          customer_logo: logoUrl,
        }),
      });

      toaster.push(successMessage, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    prefillFormFields();
  }, [props.customer]);

  const isReadonly = !userRoles.includes("superuser");

  return (
    <Container>
      <SectionRibbon
        title="Customer Settings"
        icon={SettingIcon}
        variant="primary"
        rightContent={
          <Badge variant={isReadonly ? "neutral" : "primary"} size="small">
            {isReadonly ? "Read Only" : "Editable"}
          </Badge>
        }
      />

      <ContentGrid>
        <Card variant="glass" animated visible>
          <CardContent>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>

            <FormFields>
              <Input
                label="Customer ID"
                value={customerID}
                disabled
                helpText="Customer ID cannot be changed"
              />

              <Input
                label="Customer Name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={isReadonly}
                placeholder="Enter customer name"
                helpText={
                  isReadonly
                    ? "Contact an administrator to edit"
                    : "This name will appear throughout the system"
                }
              />
            </FormFields>
          </CardContent>
        </Card>

        <Card variant="glass" animated visible delay={100}>
          <CardContent>
            <CardHeader>
              <CardTitle>Logo & Branding</CardTitle>
            </CardHeader>

            <LogoSection>
              <LogoPreviewContainer>
                {logoPreview ? (
                  <LogoPreview src={logoPreview} alt="Customer Logo" />
                ) : (
                  <LogoPlaceholder>
                    <div>No logo uploaded</div>
                  </LogoPlaceholder>
                )}
              </LogoPreviewContainer>

              <UploadSection>
                <UploadLabel>Upload Logo</UploadLabel>
                <UploadHelp>
                  Recommended: PNG or JPG, max 2MB, square format works best
                </UploadHelp>

                <StyledUploader
                  listType="picture"
                  fileList={
                    logoFile ? [{ name: logoFile.name, url: logoPreview }] : []
                  }
                  onChange={(fileList) => {
                    if (fileList.length > 0) {
                      const file = fileList[0].blobFile || fileList[0];
                      setLogoFile(file);

                      if (file && file.type && file.type.startsWith("image/")) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setLogoPreview(e.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    } else {
                      setLogoFile(null);
                      setLogoPreview(customerLogo);
                    }
                  }}
                  accept="image/*"
                  removable
                  multiple={false}
                  disabled={isReadonly}
                >
                  <UploadButton variant="secondary" disabled={isReadonly}>
                    <Icon_Upload style={{ marginRight: "8px" }} />
                    Choose File
                  </UploadButton>
                </StyledUploader>

                {isReadonly && (
                  <ReadonlyNote>
                    Contact an administrator to upload a new logo
                  </ReadonlyNote>
                )}
              </UploadSection>
            </LogoSection>
          </CardContent>
        </Card>
      </ContentGrid>

      <ActionBar>
        <Button variant="subtle" onClick={prefillFormFields} disabled={loading}>
          Reset Changes
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          loading={loading}
          disabled={isReadonly}
        >
          Save Changes
        </Button>
      </ActionBar>
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LogoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LogoPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
`;

const LogoPreview = styled.img`
  max-width: 200px;
  max-height: 120px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: ${shadows.sm};
`;

const LogoPlaceholder = styled.div`
  width: 200px;
  height: 120px;
  border: 2px dashed ${colors.neutral[300]};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.neutral[500]};
  font-size: 14px;
  background: rgba(0, 0, 0, 0.02);
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UploadLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: ${colors.neutral[800]};
`;

const UploadHelp = styled.div`
  font-size: 12px;
  color: ${colors.neutral[600]};
  margin-bottom: 8px;
`;

const StyledUploader = styled(Uploader)`
  width: 100%;

  .rs-uploader-trigger-btn {
    border: none !important;
    padding: 0 !important;
    background: none !important;
    width: 100% !important;
    display: flex !important;
    justify-content: flex-start !important;
    overflow: visible !important;
  }

  .rs-uploader-file-list {
    margin-top: 12px;
  }

  .rs-uploader-file-item {
    border-radius: 8px;
    border: 1px solid ${colors.neutral[200]};
    background: rgba(255, 255, 255, 0.5);
  }
`;

const UploadButton = styled(Button)`
  width: auto;
  min-width: 140px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0;
  padding: 12px 24px !important;
`;

const ReadonlyNote = styled.div`
  font-size: 12px;
  color: ${colors.neutral[500]};
  font-style: italic;
  margin-top: 4px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid ${colors.neutral[200]};

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const successMessage = {
  type: "success",
  header: "Success",
  description: "Customer settings updated successfully!",
};

const errorMessage = {
  type: "error",
  header: "Error",
  description: "Something went wrong. Please try again.",
};
